import { toast } from "react-toastify";
import { makeRequest } from "./apiRequests";
import { ROUTE_CONSTANT } from "../routes/constant";
import { handleApiError, setAuthToken } from "../utils/authHelpers";
import { decodeMojibake, normalizeTranslationLabel } from "../utils/utils";

async function loginAdmin(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/login-admin",
      payload,
      config
    );
    if (status > 200 && data) {
      toast.success(data.message);
      setAuthToken(data.data);
      navigate(ROUTE_CONSTANT.COMMON.DASHBOARD);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getProfileDetails(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/admin-profile`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function changePassword(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/change-password`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.AUTH.SHOW_PROFILE);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function forgetPassword(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/forget-password`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.AUTH.LOGIN);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function resetPassword(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/reset-password`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.AUTH.LOGIN);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
    navigate(ROUTE_CONSTANT.AUTH.FORGET_PASSWORD);
  }
}

async function getNews(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-news-list`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      return false;
    }
  } catch (error) {
    handleApiError(error);
  }
}


async function runNewsWorkflowCommand(endpoint, payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/${endpoint}`,
      payload,
      config
    );
    if (status >= 200 && status < 300) {
      toast.success(data?.message || "Editorial workflow updated");
      return data;
    }
    toast.error(data?.message || "Editorial workflow action failed");
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

const submitNewsForReview = (payload, config, navigate) =>
  runNewsWorkflowCommand("submit-news-for-review", payload, config, navigate);
const approveNewsRevision = (payload, config, navigate) =>
  runNewsWorkflowCommand("approve-news", payload, config, navigate);
const requestNewsCorrections = (payload, config, navigate) =>
  runNewsWorkflowCommand("request-news-corrections", payload, config, navigate);
const scheduleNewsRevision = (payload, config, navigate) =>
  runNewsWorkflowCommand("schedule-news", payload, config, navigate);
const publishNewsRevision = (payload, config, navigate) =>
  runNewsWorkflowCommand("publish-news", payload, config, navigate);
const revalidateNewsRevision = (payload, config, navigate) =>
  runNewsWorkflowCommand("newsroom/revalidate", payload, config, navigate);

async function getNewsroomCapabilities(config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/newsroom/capabilities",
      {},
      config
    );
    return status === 200 ? data : null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function getNewsroomControl(config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/newsroom/control",
      {},
      config
    );
    return status === 200 ? data : null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function updateNewsroomControl(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      "/admin/newsroom/control",
      payload,
      config
    );
    if (status >= 200 && status < 300) {
      toast.success(data?.message || "Newsroom settings saved");
      return data;
    }
    toast.error(data?.message || "Failed to save newsroom settings");
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function listNewsroomRuns(payload = {}, config = {}, navigate) {
  try {
    const query = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        query.set(key, value);
      }
    });
    const { data, status } = await makeRequest(
      "GET",
      `/admin/newsroom/runs?${query.toString()}`,
      {},
      config
    );
    return status === 200 ? data : null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function startNewsroomRun(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/newsroom/runs",
      payload,
      config
    );
    if (status >= 200 && status < 300) {
      toast.success(data?.message || "Newsroom draft run started");
      return data;
    }
    toast.error(data?.message || "Failed to start newsroom run");
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function getNewsroomRunEvidence(runId, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/newsroom/runs/${encodeURIComponent(runId)}/evidence`,
      {},
      config
    );
    return status === 200 ? data : null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function cancelNewsroomRun(runId, payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/newsroom/runs/${encodeURIComponent(runId)}/cancel`,
      payload,
      config
    );
    if (status >= 200 && status < 300) {
      toast.success(data?.message || "Newsroom run cancelled");
      return data;
    }
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function retryNewsroomRun(runId, payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/newsroom/runs/${encodeURIComponent(runId)}/retry`,
      payload,
      config
    );
    if (status >= 200 && status < 300) {
      toast.success(data?.message || "Newsroom run queued again");
      return data;
    }
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function getNewsDetails(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-news?newsId=${payload.newsId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.info(data.message);
      return false;
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addNews(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/add-news",
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.NEWS);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addNewsCoverPic(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/upload-news-avatar",
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function uploadNewsVideo(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/upload-news-video",
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteNews(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/delete-news?newsId=${payload.newsId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.NEWS);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addTestimonial(payload, config, navigate) {
  try {
    const { status, data } = await makeRequest(
      "POST",
      `/admin/add-testimonial`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.TESTIMONIALS);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addTestimonialProfilePic(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/upload-testimonial-avatar",
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getTestimonialList(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-testimonials-list`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.info(data.message);
      return false;
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteTestimonial(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/delete-testimonial?tid=${payload.testimonialId}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getHotSongCard(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/get-actual-hot-album",
      payload,
      config
    );
    if (status === 200) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error, navigate);
  }
}

async function deleteHotAlbum(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-song?hotAlbumId=${payload.hotSongId}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.HOT_ALBUMS);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getTopChartCard(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/top-chart-list",
      payload,
      config
    );
    if (status === 200) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error, navigate);
  }
}

async function deleteTopChart(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-top-chart?chartId=${payload.chartId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.TOP_CHARTS);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getFeedback(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/get-user-feedbacks-list",
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function editProfile(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/edit-admin-profile`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.AUTH.SHOW_PROFILE);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function editProfilePicure(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-profile-picture`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.AUTH.SHOW_PROFILE);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function displayLyrics(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/get-admin-lyrics`,
      payload,
      config
    );
    if (status === 201 && data) {
      //toast.success(data.message);
      return data;
    } else {
      // toast.error(data.message)
      return false;
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addHotSongs(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-hot-album`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.HOT_ALBUMS);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addHotAlbums(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-actual-hot-album`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.HOT_ALBUMS);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function albumSongs(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-album-songs?album_id=${payload.album_id}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      return false;
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function artistSongs(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/artist/song?artistId=${payload.artistId}&page=${payload.page}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      return false;
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteActualHotAlbum(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-actual-hot-album?albumId=${payload.hotSongId}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.HOT_ALBUMS);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

/** V2 APIs */
async function getTrendingArtists(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-trending-artists`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getRecommendedArtists(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-recommended-artists`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSearchArtist(payload = {}, config = {}, navigation) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/search-artist`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addTrendingArtist(payload, config, navigation) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-trending-artist`,
      payload,
      config
    );

    if (status === 201 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to add trending artist.");
    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function patchUpdateRecArtistPriority(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/update-rec-artist-priority`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addRecommendedArtist(payload, config, navigation) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-recommended-artist`,
      payload,
      config
    );

    if (status === 201 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to add recommended artist.");
    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function patchUpdateTrendingArtistPriority(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/update-trending-artist-priority`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteTrendingArtist(payload, config, navigate) {
  try {
    // Make the DELETE request with ID in the body and headers
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-trending-artist`, // No ID in the URL
      payload, // Send the ID in the request body
      config
    );

    if (status === 200 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to delete trending artist.");
    return null;
  } catch (error) {
    handleApiError(error); // Handle any API errors
    return null;
  }
}

async function deleteRecommendedArtist(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-recommended-artist`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to delete recommended artist.");
    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function invalidateCacheByPattern(pattern, config = {}) {
  try {
    await clearCacheByPattern(pattern, config, true);
  } catch (error) {
    console.error(`Failed to invalidate cache for pattern ${pattern}:`, error);
  }
}

async function getTopExploreAlbums(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-top-explore-albums`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function patchUpdateTopAlbumPriority(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/update-top-album-priority`,
      payload,
      config
    );
    if (status >= 200 && data) {
      await invalidateCacheByPattern("get-top-explore-albums*", config);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSearchAlbum(payload = {}, config = {}, navigation) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/search-album`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addTopAlbum(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-top-album`,
      payload,
      config
    );
    if (status === 201 && data) {
      await invalidateCacheByPattern("get-top-explore-albums*", config);
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.HOT_ALBUMS);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function createAlbum(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/create-album`,
      payload,
      config
    );
    if (status === 201 && data) {
      return data;
    } else {
      toast.error(data.message);
      return data;
    }
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

async function uploadAlbumImage(formData, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-album-image`,
      formData,
      config
    );
    if (status === 200 || status === 201) {
      return data;
    } else {
      toast.error(data?.message || "Failed to upload image");
      return data;
    }
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

async function deleteTopExploreAlbum(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-top-explore-album`,
      payload,
      config
    );
    if (status === 200 && data) {
      await invalidateCacheByPattern("get-top-explore-albums*", config);
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAllAdminPlaylist(payload, config, navigation) {
  try {
    const url = `/admin/get-all-admin-playlist?`;
    const { data, status } = await makeRequest("GET", url, payload, config);
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getPlaylistSong(payload, config, navigation) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-playlist-songs`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSongRecommendedList(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-recommended-song-list`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getPopularSongs(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/most-popular-song-list`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSearchSong(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/search-song`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
      return [];
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addMostPopularSongs(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-most-popular-song`,
      payload,
      config
    );
    if (status === 201 && data) {
      await invalidateCacheByPattern("get-most-popular-songs-list*", config);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteMostPopularSongs(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-popular-song`,
      payload,
      config
    );
    if (status === 200 && data) {
      await invalidateCacheByPattern("get-most-popular-songs-list*", config);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function createAdminPlaylist(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/create-admin-playlist`,
      payload,
      config
    );
    if (status === 201 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function uploadPlaylistImage(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-playlist-picture`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addRecommendedSong(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-recommended-song`,
      payload,
      config
    );
    if (status === 201 && data) {
      await invalidateCacheByPattern("get-recommended-songs-list*", config);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteRecommendedSong(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-recommended-song`,
      payload,
      config
    );
    if (status === 200 && data) {
      await invalidateCacheByPattern("get-recommended-songs-list*", config);
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getMostPlayedSong(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-most-played-song`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addMostPlayedSong(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-most-played-song`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteMostPlayedSong(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-most-played-song`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteAdminPlaylist(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-admin-playlist`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function patchRemoveAdminPlaylistSong(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/remove-admin-playlist-song`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function patchUpdateAdminPlaylist(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/update-admin-playlist`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function toggleTopPlaylist(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/toggle-top-playlist`,
      payload,
      config
    );
    if (status >= 200 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function toggleExplorePlaylist(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/toggle-explore-playlist`,
      payload,
      config
    );
    if (status >= 200 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function patchUpdatePlaylistPriority(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/update-playlist-priority`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getGenres(payload = {}, config = {}, navigate) {
  try {
    // If config is null (no auth token), prevent the API call
    if (!config) {
      toast.error("Authentication required. Please log in.");
      if (navigate) {
        navigate(ROUTE_CONSTANT.AUTH.LOGIN);
      }
      return null;
    }

    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-genres`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error, navigate);
  }
}

async function getPageConfigTargets(config = {}, navigate) {
  try {
    if (!config) {
      toast.error("Authentication required. Please log in.");
      if (navigate) {
        navigate(ROUTE_CONSTANT.AUTH.LOGIN);
      }
      return null;
    }

    const { data, status } = await makeRequest(
      "GET",
      `/admin/page-config-targets`,
      {},
      config
    );

    if (status === 200 && data) {
      return data;
    }

    toast.error(data?.message || "Failed to fetch page config targets");
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function getPageConfig(payload = {}, config = {}, navigate) {
  try {
    if (!config) {
      toast.error("Authentication required. Please log in.");
      if (navigate) {
        navigate(ROUTE_CONSTANT.AUTH.LOGIN);
      }
      return null;
    }

    const { data, status } = await makeRequest(
      "GET",
      `/admin/page-config`,
      payload,
      config
    );

    if (status === 200 && data) {
      return data;
    }

    toast.error(data?.message || "Failed to fetch page configuration");
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function savePageConfig(payload, config = {}, navigate) {
  try {
    if (!config) {
      toast.error("Authentication required. Please log in.");
      if (navigate) {
        navigate(ROUTE_CONSTANT.AUTH.LOGIN);
      }
      return null;
    }

    const { data, status } = await makeRequest(
      "POST",
      `/admin/page-config`,
      payload,
      config
    );

    if (status >= 200 && status < 300 && data) {
      toast.success(data.message || "Page config saved successfully");
      return data;
    }

    toast.error(data?.message || "Failed to save page configuration");
    return null;
  } catch (error) {
    handleApiError(error, navigate);
    return null;
  }
}

async function addGenres(genres, config) {
  try {
    // If config is null (no auth token), prevent the API call
    if (!config) {
      toast.error("Authentication required. Please log in.");
      return null;
    }

    const response = await makeRequest(
      "POST",
      `/admin/add-genres`,
      { genres },
      config
    );

    const { data, status } = response;

    if (status >= 200 && status < 300 && data?.success) {
      toast.success(data?.message || "Genre added successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to add genre");
      return null;
    }
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function createGenre(genreData, config) {
  try {
    // If config is null (no auth token), prevent the API call
    if (!config) {
      toast.error("Authentication required. Please log in.");
      return null;
    }

    const { data, status } = await makeRequest(
      "POST",
      `/admin/create-genre`,
      genreData,
      config
    );
    if (status === 201 && data) {
      toast.success("Genre created successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to create genre");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getGenreById(genreId, config) {
  try {
    // If config is null (no auth token), prevent the API call
    if (!config) {
      toast.error("Authentication required. Please log in.");
      return null;
    }

    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-genre/${genreId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch genre");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function updateGenre(genreId, updateData, config) {
  try {
    // If config is null (no auth token), prevent the API call
    if (!config) {
      toast.error("Authentication required. Please log in.");
      return null;
    }

    const { data, status } = await makeRequest(
      "PUT",
      `/admin/update-genre/${genreId}`,
      updateData,
      config
    );
    if (status === 200 && data) {
      toast.success("Genre updated successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to update genre");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteGenre(genreId, config) {
  try {
    // If config is null (no auth token), prevent the API call
    if (!config) {
      toast.error("Authentication required. Please log in.");
      return null;
    }

    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-genre/${genreId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success("Genre deleted successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to delete genre");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addSongToAdminPlayList(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-song-to-playlist`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAllQuizs(config = {}, payload, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `admin/get-all-quizzes`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function uploaduizCsv(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `admin/add-quiz`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

// --- QUIZ ---
async function getQuizById(quizId, config = {}) {
  return makeRequest("GET", `/admin/edit-quiz/${quizId}`, {}, config).then(r => r.data);
}

async function updateQuiz(quizId, payload, config = {}) {
  // JSON update (no file upload here)
  return makeRequest("PUT", `/admin/update-quiz/${quizId}`, payload, config).then(r => r.data);
}

async function getContactus(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/get-contact-us",
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addQuiz(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `admin/add-single-quiz`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getTopArtists(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/get-top-artists",
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addTopArtist(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `admin/add-top-artist`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteTopArtist(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `admin/delete-top-artist`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getTopSong(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/get-top-songs",
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addTopSong(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `admin/add-top-song`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteTopSong(payload, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `admin/delete-top-songs`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAutomatedReleaseControl(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/automatic-releases/control",
      {},
      config
    );
    if (status === 200 && data) return data;
    toast.error(data?.message || "Failed to load automated release settings");
  } catch (error) {
    handleApiError(error);
  }
}

async function updateAutomatedReleaseControl(payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      "/admin/automatic-releases/control",
      payload,
      config
    );
    if (status === 200 && data) return data;
    toast.error(data?.message || "Failed to save automated release settings");
  } catch (error) {
    handleApiError(error);
  }
}

async function runAutomatedReleasePublication(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      "/admin/automatic-releases/run",
      payload,
      config
    );
    if (status === 200 || status === 201) return data;
    toast.error(data?.message || "Automated release publication failed");
  } catch (error) {
    handleApiError(error);
  }
}

async function getNewReleaseSong(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/get-new-release-songs",
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAllContributeToLyrics(config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-all-lyrics`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getLyricsById(config = {}, lyricsId, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-lyrics-by-id?lyrics_id=${lyricsId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function editArtistBio(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/edit-artist-bio`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message);
      navigate(ROUTE_CONSTANT.COMMON.ARTIST_BIO);
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAllPlaylistBio(payload = {}, config = {}, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/artist-bio-list`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getArtistBioDetails(config = {}, artist_name, navigate) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `user/get-artist-bio-with-top?artist_name=${artist_name}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function uploadNewsImage(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-news-avatar`,
      payload,
      config
    );

    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function putUpdateNews(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/update-news`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function generateNewsArticle(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/generate-news-article`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to generate article");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function suggestNewsTopics(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/suggest-news-topics`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to suggest topics");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function uploadAuthorImage(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-author-picture`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function researchNewsTopics(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/research-news-topics`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to research topics");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function writeNewsArticle(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/write-news-article`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to write article");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function getNewsGenerationStatus(jobId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/news-generation/status/${jobId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch news generation status");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function getAuthors(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-authors`,
      {},
      config
    );
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch authors");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function addAuthor(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/add-author`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data?.message || "Author created successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to create author");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function updateAuthor(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/update-author`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data?.message || "Author updated successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to update author");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function deleteAuthor(authorId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/delete-author?authorId=${authorId}`,
      {},
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data?.message || "Author deleted successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to delete author");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function uploadAuthorPersonaImage(payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-author-image`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to upload author image");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function seedAuthorPersonas(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/seed-author-personas`,
      {},
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data?.message || "Author personas seeded");
      return data;
    } else {
      toast.error(data?.message || "Failed to seed authors");
      return false;
    }
  } catch (error) {
    handleApiError(error);
    return false;
  }
}

async function uploadBannerImage(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-banner-image`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function approveOrrejectLyrics(payload, config, navigate) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/process-contribution`,
      payload,
      config
    );
    if (status === 201 && data) {
      return status;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getNewsSubscriber(config = {}, page) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-all-news-latter?page=${page}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getArtistSongAndAlbums(config = {}, payload) {
  try {
    const params = new URLSearchParams({
      artist_id: payload.artist_id,
      ...(payload.albums_page && { albums_page: payload.albums_page }),
      ...(payload.albums_limit && { albums_limit: payload.albums_limit }),
      ...(payload.songs_page && { songs_page: payload.songs_page }),
      ...(payload.songs_limit && { songs_limit: payload.songs_limit }),
    });
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-artist-songs-albums?${params.toString()}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getBanners(config = {}, payload) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-banners?page=${payload.page}&limit=${payload.limit}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function addBanner(config = {}, payload) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/create-banner`,
      payload,
      config
    );
    if (status === 201 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function editBanner(config = {}, payload) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/update-banner`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteBanner(config = {}, payload) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-banner`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function toggleStatusFun(config = {}, payload) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/update-status`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getYoutubeVideos(config = {}, payload) {
  try {
    const genreParam = payload.genre ? `&genre=${encodeURIComponent(payload.genre)}` : "";
    const { data, status } = await makeRequest(
      "GET",
      `/admin/get-all-youtube-videos?page=${payload.page}&limit=${payload.limit}${genreParam}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSearchYoutubeVideos(payload = {}, config = {}, navigation) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/search-youtube-video`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteContribution(payload = {}, config = {}) {
  try {
    if (!payload.id) throw new Error("Id to Delete Not Provided");
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-contribution/${payload.id}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success("Contribution Deleted Successfully");
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Song Lyrics Contributions (for existing songs)
async function getAllSongLyricsContributions(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `admin/get-all-song-lyrics-contributions?${queryString}`
      : `admin/get-all-song-lyrics-contributions`;

    const { data, status } = await makeRequest(
      "GET",
      url,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSongLyricsContributionById(config = {}, id) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-song-lyrics-contribution-by-id?id=${id}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function processSongLyricsContribution(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/process-song-lyrics-contribution`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contribution');
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteSongLyricsContribution(payload = {}, config = {}) {
  try {
    if (!payload.id) throw new Error("Id to Delete Not Provided");
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-song-lyrics-contribution/${payload.id}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success("Song Lyrics Contribution Deleted Successfully");
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function bulkProcessSongLyricsContributions(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/bulk-process-song-lyrics-contributions`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contributions');
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Album Contributions
async function getAllAlbumContributions(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `admin/get-all-album-contributions?${queryString}`
      : `admin/get-all-album-contributions`;

    const { data, status } = await makeRequest(
      "GET",
      url,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAlbumContributionById(config = {}, id) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-album-contribution-by-id?id=${id}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function processAlbumContribution(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/process-album-contribution`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contribution');
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteAlbumContribution(payload = {}, config = {}) {
  try {
    if (!payload.id) throw new Error("Id to Delete Not Provided");
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-album-contribution/${payload.id}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success("Album Contribution Deleted Successfully");
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function bulkProcessAlbumContributions(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/bulk-process-album-contributions`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contributions');
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Song Contributions
async function getAllSongContributions(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `admin/get-all-song-contributions?${queryString}`
      : `admin/get-all-song-contributions`;

    const { data, status } = await makeRequest(
      "GET",
      url,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getSongContributionById(config = {}, id) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-song-contribution-by-id?id=${id}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function processSongContribution(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/process-song-contribution`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contribution');
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteSongContribution(payload = {}, config = {}) {
  try {
    if (!payload.id) throw new Error("Id to Delete Not Provided");
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-song-contribution/${payload.id}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success("Song Contribution Deleted Successfully");
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function bulkProcessSongContributions(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/bulk-process-song-contributions`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contributions');
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Artist Bio Contributions
async function getAllArtistBioContributions(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `admin/get-all-artist-bio-contributions?${queryString}`
      : `admin/get-all-artist-bio-contributions`;

    const { data, status } = await makeRequest(
      "GET",
      url,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getArtistBioContributionById(config = {}, id) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-artist-bio-contribution-by-id?id=${id}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function processArtistBioContribution(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/process-artist-bio-contribution`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contribution');
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteArtistBioContribution(payload = {}, config = {}) {
  try {
    if (!payload.id) throw new Error("Id to Delete Not Provided");
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-artist-bio-contribution/${payload.id}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success("Artist Bio Contribution Deleted Successfully");
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function bulkProcessArtistBioContributions(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/bulk-process-artist-bio-contributions`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contributions');
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Video Contributions
async function getAllVideoContributions(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `admin/get-all-video-contributions?${queryString}`
      : `admin/get-all-video-contributions`;

    const { data, status } = await makeRequest(
      "GET",
      url,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getVideoContributionById(config = {}, id) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `admin/get-video-contribution-by-id?id=${id}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function processVideoContribution(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/process-video-contribution`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contribution');
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteVideoContribution(payload = {}, config = {}) {
  try {
    if (!payload.id) throw new Error("Id to Delete Not Provided");
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-video-contribution/${payload.id}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success("Video Contribution Deleted Successfully");
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function bulkProcessVideoContributions(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/bulk-process-video-contributions`,
      payload,
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message);
      return data;
    } else {
      toast.error(data?.message || 'Failed to process contributions');
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Content Pre-warming APIs
async function startPrewarmJob(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.startIndex) queryParams.append('startIndex', params.startIndex);
    if (params.batchSize) queryParams.append('batchSize', params.batchSize);
    if (params.delay) queryParams.append('delay', params.delay);
    if (params.contentTypes) queryParams.append('contentTypes', params.contentTypes);
    if (params.forceRegenerate) queryParams.append('forceRegenerate', params.forceRegenerate);

    const { data, status } = await makeRequest(
      "POST",
      `/admin/prewarm/start?${queryParams.toString()}`,
      {},
      config
    );
    if (status === 202 && data) {
      toast.success(data.message || "Pre-warming job started");
      return data;
    } else {
      toast.error(data?.message || "Failed to start pre-warming job");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getPrewarmJobStatus(jobId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/prewarm/status/${jobId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch job status");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAllPrewarmJobs(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/prewarm/jobs`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch jobs");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function cancelPrewarmJob(jobId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/prewarm/cancel/${jobId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Job cancelled");
      return data;
    } else {
      toast.error(data?.message || "Failed to cancel job");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function checkArtistContent(artistId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/prewarm/check/${artistId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to check artist content");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function searchArtistsForPrewarm(search, limit = 100, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/prewarm/search-artists?search=${encodeURIComponent(search)}&limit=${limit}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to search artists");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function previewCsvArtists(geniusIds, page = 1, limit = 50, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/prewarm/preview-csv?page=${page}&limit=${limit}`,
      { geniusIds },
      config
    );
    // Accept any 2xx status code
    if (status >= 200 && status < 300 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to preview artists");
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Batch Pre-warming APIs (50% cheaper, async processing)
async function startBatchPrewarmJob(geniusIds, params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.contentTypes) queryParams.append('contentTypes', params.contentTypes);
    if (params.forceRegenerate) queryParams.append('forceRegenerate', params.forceRegenerate);

    const { data, status } = await makeRequest(
      "POST",
      `/admin/prewarm/batch/start?${queryParams.toString()}`,
      { geniusIds },
      config
    );
    const payload = data?.data || data;
    const hasJobIds =
      !!payload?.jobId ||
      (Array.isArray(payload?.jobIds) && payload.jobIds.length > 0);
    const isAcceptedHttp = status >= 200 && status < 300;
    const isAcceptedBody = data?.success !== false;
    const message = data?.message || "";
    const indicatesStarted = /batch job[s]?\s+started/i.test(message);

    if (isAcceptedHttp && isAcceptedBody && (hasJobIds || indicatesStarted)) {
      toast.success(message || "Batch job started successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to start batch job");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getBatchJobStatus(jobId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/prewarm/batch/status/${jobId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch batch job status");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAllBatchJobs(statusFilter, config = {}, page = 1, limit = 20) {
  try {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('page', String(page));
    params.set('limit', String(limit));
    const url = `/admin/prewarm/batch/jobs?${params.toString()}`;
    const { data, status } = await makeRequest(
      "GET",
      url,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch batch jobs");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function cancelBatchJob(jobId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/prewarm/batch/cancel/${jobId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Batch job cancelled successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to cancel batch job");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteBatchJob(jobId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/prewarm/batch/delete/${jobId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Batch job deleted successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to delete batch job");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function rerunBatchJob(jobId, params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.forceRegenerate) queryParams.append('forceRegenerate', params.forceRegenerate);
    const query = queryParams.toString();
    const endpoint = query
      ? `/admin/prewarm/batch/rerun/${jobId}?${query}`
      : `/admin/prewarm/batch/rerun/${jobId}`;

    const { data, status } = await makeRequest("POST", endpoint, {}, config);
    const payload = data?.data || data;
    const hasJobIds = Array.isArray(payload?.jobIds) && payload.jobIds.length > 0;
    if ((status === 200 || status === 202) && data?.success !== false && hasJobIds) {
      toast.success(data.message || "Batch re-run started successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to re-run batch job");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function regenerateBadBios(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.dryRun) queryParams.append('dryRun', params.dryRun);
    if (params.maxArtists) queryParams.append('maxArtists', params.maxArtists);

    const { data, status } = await makeRequest(
      "POST",
      `/admin/prewarm/batch/regenerate-bad-bios?${queryParams.toString()}`,
      {},
      config
    );
    if ((status === 200 || status === 202) && data) {
      toast.success(data.message || "Bad bio regeneration initiated");
      return data;
    } else {
      toast.error(data?.message || "Failed to regenerate bad bios");
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Tags Management APIs
async function getTags(params = {}, config = {}) {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/admin/tags?${queryString}` : '/admin/tags';
    const { data, status } = await makeRequest("GET", url, {}, config);
    if (status === 200 && data) {
      return data;
    }
    return { data: { tags: [] } };
  } catch (error) {
    handleApiError(error);
    return { data: { tags: [] } };
  }
}

async function createTag(payload, config = {}) {
  try {
    const { data, status } = await makeRequest("POST", "/admin/tags", payload, config);
    if (status === 201 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to create tag");
    return null;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

async function updateTag(payload, config = {}) {
  try {
    const { data, status } = await makeRequest("PUT", "/admin/tags", payload, config);
    if (status === 200 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to update tag");
    return null;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

async function deleteTag(tagId, config = {}) {
  try {
    const { data, status } = await makeRequest("DELETE", `/admin/tags/${tagId}`, {}, config);
    if (status === 200 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to delete tag");
    return null;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

async function getTagBySlug(slug, config = {}) {
  try {
    const { data, status } = await makeRequest("GET", `/admin/tags/slug/${slug}`, {}, config);
    if (status === 200 && data) {
      return data;
    }
    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function getFeaturedTags(config = {}) {
  try {
    const { data, status } = await makeRequest("GET", "/admin/tags/featured", {}, config);
    if (status === 200 && data) {
      return data;
    }
    return { data: [] };
  } catch (error) {
    handleApiError(error);
    return { data: [] };
  }
}

// Cache Management APIs
async function clearAllCache(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/cache/clear`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "All cache cleared successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to clear cache");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function clearCacheByPattern(pattern, config = {}, suppressToast = false) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/cache/clear-by-pattern?pattern=${encodeURIComponent(pattern)}`,
      {},
      config
    );
    if (status === 200 && data) {
      if (!suppressToast) {
        toast.success(data.message || `Cache cleared for pattern: ${pattern}`);
      }
      return data;
    } else {
      if (!suppressToast) {
        toast.error(data?.message || "Failed to clear cache");
      }
    }
  } catch (error) {
    if (!suppressToast) {
      handleApiError(error);
    }
  }
}

async function clearNewsCache(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/cache/clear-news`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "News cache cleared successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to clear news cache");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function clearPlaylistsCache(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/cache/clear-playlists`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Playlists cache cleared successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to clear playlists cache");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function pingRedis(payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/cache/ping`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Redis connection check failed");
    }
  } catch (error) {
    handleApiError(error);
  }
}

// User Activity Tracking APIs
async function getUsersActivity(page = 1, limit = 50, search = "", config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/users-activity?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch users activity");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getUserActivityDetail(userId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/user-activity/${userId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch user activity");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getActivityLeaderboard(limit = 20, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/activity-leaderboard?limit=${limit}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch activity leaderboard");
    }
  } catch (error) {
    handleApiError(error);
  }
}

// Read-only, aggregate engagement and experiment reporting.
async function getEngagementReport(params = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      "/admin/analytics/engagement-report",
      params,
      config
    );
    if (status === 200 && data) {
      return data;
    }
    toast.error(data?.message || "Failed to fetch engagement report");
    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

async function getPodcasts({ page, search }) {
  try {
    const { data, status } = await makeRequest(
      'GET',
      `/user/podcasts?page=${page}&search=${search}`
    )
    if (status === 200 && data) {
      //toast.success("Podcast Successfully Fetched")
      return data
    }
    else toast.error("Some Error Occured While Getting the Podcast")
  } catch (error) {
    handleApiError(error)
  }
}

async function editPodcast(id, formData) {
  try {
    const { status } = await makeRequest(
      'PUT',
      `/user/podcasts/${id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    if (status === 200) {
      toast.success("Podcast Updated Successfully")
    } else toast.error("Failed To Update Podcast")
  } catch (error) {
    handleApiError(error)
  }
}

async function createPodcast(formData) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      '/user/podcasts',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    if (status === 201 && data) {
      return data
    }
    else toast.error("Failed To Create a Podcast")
  } catch (error) {
    handleApiError(error)
  }
}

async function deletePodcast(id) {
  try {
    const { status } = await makeRequest(
      "DELETE",
      `/user/podcasts/${id}`,
    )
    if (status === 200) {
      toast.success("Podcast Deleted Successfully")
    }
    else toast.error("Failed To Delete Podcast")
  } catch (error) {
    handleApiError(error)
  }
}

async function getEpisodes({ podcastId, page, search }) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/user/episodes?podcastId=${podcastId}&page=${page}&search=${search}`,
    )
    if (status === 200 && data) {
      return data
    }
    else toast.error("Failed To Get Episodes")
  } catch (error) {
    handleApiError(error)
  }
}

async function createEpisode(episodeData) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/user/episodes`,
      episodeData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    )
    if (status === 201 && data) {
      return data
    }
    else toast.error("Faile To Add Episode For Podcast")
  } catch (error) {
    handleApiError(error)
  }
}

async function deleteEpisode(id) {
  try {
    const { status } = await makeRequest(
      "DELETE",
      `/user/episodes/${id}`
    )
    if (status === 200) toast.success("Episode Deleted Successfully")
    else toast.error("Failed To Delete Episode")
  } catch (error) {
    handleApiError(error)
  }
}

async function deleteEpisodeAudio(id) {
  try {
    const { status } = await makeRequest(
      "DELETE",
      `/user/episodes/${id}/audio`
    )
    if (status === 200) toast.success("Audio Successfully Removed")
    else toast.error("Failed to Remove Audio")
  } catch (error) {
    handleApiError(error)
  }
}

async function editEpisode(id, podcastId, data) {
  try {
    const { status } = await makeRequest(
      "PUT",
      `/user/episodes/${id}?podcastId=${podcastId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    )
    if (status === 200) toast.success("Episode Edited Successfully")
    else toast.error("Failed to Update Episode")
  } catch (error) {
    handleApiError(error)
  }
}

async function updateEpisodeAudio(id, formData, podcastId) {
  try {
    const { status } = await makeRequest(
      "PUT",
      `/user/episodes/${id}/audio?podcastId=${podcastId}`,
      formData,
      {
        headers: {
          "content-Type": "multipart/form-data"
        }
      }
    )
    if (status === 200) toast.success("Episode Audio Updated Successfully")
    else toast.error("Failed to Update Episode Audio")

  } catch (error) {
    handleApiError(error)
  }
}

async function addToMusicVideoSection(config, song_id) {
  try {
    const { status } = await makeRequest(
      "POST",
      '/admin/add-youtube-video',
      { song_id },
      config
    )
    if (status === 201) {
      await invalidateCacheByPattern("get-all-youtube-videos*", config);
      toast.success("Song Added Successfully to music video section")
    }
    else toast.error("Failed to Add Song in Music Video Section")
  } catch (error) {
    handleApiError(error)
  }
}

async function removeFromMusicVideoSection(song_id, config) {
  try {
    const { status } = await makeRequest(
      "DELETE",
      `/admin/delete-youtube-video/${song_id}`,
      {},
      config

    )
    if (status === 200) {
      await invalidateCacheByPattern("get-all-youtube-videos*", config);
      toast.success("Song Successfully Removed From Music Video Section")
    }
    else toast.error("Failed To Remove Song From Music Video Section")
  } catch (error) {
    handleApiError(error)
  }
}

async function updateFeaturedMusicVideoGenres(song_id, genres, config) {
  try {
    const { status, data } = await makeRequest(
      "POST",
      `/admin/update-youtube-video-featured`,
      { song_id, genres },
      config
    );
    if (status >= 200 && status < 300 && data) {
      toast.success(data.message || "Featured video genres updated successfully");
      return data;
    }
    toast.error(data?.message || "Failed to update featured genres");
  } catch (error) {
    handleApiError(error);
  }
}

async function addNewReleaseSong(
  song_id,
  config,
  pageTarget = {},
  genre = null
) {
  try {
    const params = new URLSearchParams();

    if (pageTarget?.pageType) {
      params.set("pageType", pageTarget.pageType);
    }

    if (pageTarget?.pageType === "genre" && pageTarget?.genreSlug) {
      params.set("genreSlug", pageTarget.genreSlug);
    }

    if (genre) {
      params.set("genre", genre);
    }

    const url = params.toString()
      ? `/admin/add-new-release/${song_id}?${params.toString()}`
      : `/admin/add-new-release/${song_id}`;
    const { status } = await makeRequest(
      "POST",
      url,
      {},
      config
    )
    if (status === 201) {
      await invalidateCacheByPattern("get-new-release-songs*", config);
      toast.success("Song Successfully Added To New Release Songs")
    }
    else throw new Error("Failed To Add New Release Song")
  } catch (error) {
    handleApiError(error)
  }
}

async function deleteNewReleaseSong(id, config) {
  try {
    const response = await makeRequest(
      "DELETE",
      `/admin/delete-new-release/${id}`,
      {}, config
    )
    if (response?.status === 200) {
      await invalidateCacheByPattern("get-new-release-songs*", config);
    }
    return response
  } catch (error) {
    handleApiError(error)
  }
}

// Add and Edit Artist Page by DigiMedia Code

// --- ARTISTS ---
async function getArtistById(artistId, config = {}) {
  return makeRequest("GET", `/admin/artist/${artistId}`, {}, config).then(r => r.data);
}

async function createArtist(formData, config = {}) {
  // multipart for image upload
  const cfg = { 
    ...config, 
    headers: { ...(config.headers || {}), "Content-Type": "multipart/form-data" } 
  };
  return makeRequest("POST", "/admin/artist", formData, cfg);
}

async function updateArtist(artistId, formData, config = {}) {
  const cfg = { 
    ...config, 
    headers: { ...(config.headers || {}), "Content-Type": "multipart/form-data" } 
  };
  return makeRequest("PUT", `/admin/artist/${artistId}`, formData, cfg).then(r => r.data);
}

// --- ARTISTS LIST + DELETE ---

// GET /admin/artists?limit=50&page=1&sort=-createdAt
async function listArtists(params = {}, config = {}) {
  const { page = 1, limit = 50, sort = "-createdAt", searchField, q } = params;

  const cfg = {
    ...config,
    params: {
      page,
      limit,
      sort,
      ...(q ? { q }: {}),
      ...(searchField ? { searchField } : {})
    },
  };

  // r.data has: { success, status, data: [...], pagination: {...} }
  return makeRequest("GET", "/admin/artists", {}, cfg).then((r) => r.data);
}

// DELETE /admin/artist/:id  (uses artist_id, not Mongo _id)
async function deleteArtist(artistId, config = {}) {
  return makeRequest("DELETE", `/admin/artist/${artistId}`, {}, config).then(
    (r) => r.data
  );
}

// ----- Songs List + DELETE ---

const normalizeSongText = (song) => {
  if (!song || typeof song !== "object") return song;
  return {
    ...song,
    song_name: normalizeTranslationLabel(decodeMojibake(song.song_name)),
    title: normalizeTranslationLabel(decodeMojibake(song.title)),
    artist: decodeMojibake(song.artist),
    artist_name: decodeMojibake(song.artist_name),
    lyrics: decodeMojibake(song.lyrics),
    description: decodeMojibake(song.description),
    song_meaning: decodeMojibake(song.song_meaning),
  };
};

// GET /admin/list-songs?limit=20&page=1&sort=-createdAt
async function listSongs(params = {}, config = {}) {
  const { page = 1, limit = 20, sort = "-createdAt", q, searchBy, artist, album } = params;

  const cfg = {
    ...config,
    params: {
      page,
      limit,
      sort,
      ...(q ? { q } : {}),
      ...(searchBy ? { searchBy } : {}),
      ...(artist ? { artist } : {}),
      ...(album ? { album } : {}),
    },
  };

  // r.data has: { success, status, data: [...], pagination: {...} }
  return makeRequest("GET", "/admin/list-songs", {}, cfg).then((r) => {
    const payload = r.data;
    const rows = Array.isArray(payload?.data)
      ? payload.data.map(normalizeSongText)
      : payload?.data;
    return { ...payload, data: rows };
  });
}


// DELETE /admin/song/:id  (uses song_id, not Mongo _id)
async function deleteSong(songId, config = {}) {
  return makeRequest("DELETE", `/admin/song/${songId}`, {}, config).then(
    (r) => r.data
  );
}

// GET /admin/songs/:songId  (songId is numeric song_id)
async function getSongById(songId, config = {}) {
  // r.data -> { success, status, data: { ...song } }
  return makeRequest("GET", `/admin/song/${songId}`, {}, config).then((r) => {
    const payload = r.data;
    return payload?.data
      ? { ...payload, data: normalizeSongText(payload.data) }
      : payload;
  });
}

// PATCH /admin/songs/:songId  (multipart FormData)
async function updateSong(songId, formData, config = {}) {
  // Let axios set multipart boundary automatically; don't set Content-Type manually if makeRequest does axios
  // r.data -> { success, status, data, message }
  return makeRequest("PATCH", `/admin/song/${songId}`, formData, config).then(r => r.data);
}

// Bulk import: upload artist CSV for a selected Spotify artistId
async function uploadArtistCsv(formData, config = {}) {
  // Ensure multipart headers
  const cfg = {
    ...config,
    headers: {
      ...(config.headers || {}),
      "Content-Type": "multipart/form-data",
    },
  };
  // If your endpoint path differs, change it here:
  return makeRequest("POST", "/admin/upload-artist-csv-file", formData, cfg);
}

// GET /admin/albums?artist_id=123&limit=100&page=1&sort=albumName
async function listAlbumsByArtist(artistId, params = {}, config = {}) {
  if (!artistId && artistId !== 0) {
    return Promise.resolve({ success: false, status: 400, data: [], pagination: null });
  }

  const {
    page = 1,
    limit = 100,
    sort = "albumName", // or "-createdAt" if you prefer
    q,
  } = params;

  const cfg = {
    ...config,
    params: {
      page,
      limit,
      sort,
      artist_id: artistId,     // <-- backend reads this to filter albums
      ...(q ? { q } : {}),
    },
  };

  // r.data -> { success, status, data: [...], pagination: {...} }
  return makeRequest("GET", "/admin/albums", {}, cfg).then((r) => r.data);
}

// GET /admin/albums - get all albums (no artist filter required)
async function getAlbums(params = {}, config = {}) {
  const {
    page = 1,
    limit = 20,
    sort = "-createdAt",
    q,
  } = params;

  const cfg = {
    ...config,
    params: {
      page,
      limit,
      sort,
      ...(q ? { q } : {}),
    },
  };

  return makeRequest("GET", "/admin/albums", {}, cfg).then((r) => r.data);
}

// PATCH /admin/update-album/:id - update an album
async function updateAlbum(albumId, payload = {}, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "PATCH",
      `/admin/update-album/${albumId}`,
      payload,
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to update album");
      return data;
    }
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// DELETE /admin/delete-album/:id - delete an album
async function deleteAlbum(albumId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/delete-album/${albumId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to delete album");
      return data;
    }
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// GET /admin/artists?sort=artist_name&q=...
async function listArtistsSimple(params = {}, config = {}) {
  const { sort = "artist_name", limit ,q } = params;
  const cfg = {
    ...config,
    params: { sort, limit ,...(q ? { q } : {}) },
  };
  // r.data -> { success, status, data: [...] }
  return makeRequest("GET", "/admin/AllArtistsOnAddSong", {}, cfg).then((r) => r.data);
}

// POST /admin/songs  (expects multipart/form-data)
async function createSong(formData, config = {}) {
  // IMPORTANT:
  // - `formData` must be a FormData instance (not a plain object)
  // - Do NOT JSON.stringify it
  // - Let the browser/axios set the multipart boundary automatically

  const cfg = {
    ...config,
    // If your getAuthConfig() already sets headers, it will merge here.
    // Usually best to NOT set Content-Type so axios can apply correct boundary.
    // headers: { 'Content-Type': 'multipart/form-data', ...(config.headers || {}) },
  };

  // r.data should be like: { success, status, data, message }
  return makeRequest("POST", "/admin/createsong", formData, cfg).then((r) => r.data);
}

async function uploadSongImage(payload, config) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/upload-song-picture`,
      payload,
      config
    );
    if (status >= 200 && data) {
      return data;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    handleApiError(error);
  }
}

// ============================================
// Admin User Management APIs
// ============================================

async function getAdminProfile(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/users/me`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    }
  } catch (error) {
    console.error("Failed to fetch admin profile:", error);
  }
}

async function listAdminUsers(params = {}, config = {}) {
  try {
    const { page = 1, limit = 20, search, role, isActive } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    if (role) queryParams.append('role', role);
    if (typeof isActive === 'boolean') queryParams.append('isActive', isActive);

    const { data, status } = await makeRequest(
      "GET",
      `/admin/users?${queryParams.toString()}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch admin users");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAdminById(adminId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/users/${adminId}`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    } else {
      toast.error(data?.message || "Failed to fetch admin details");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function createAdminUser(payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/users`,
      payload,
      config
    );
    if (status === 201 && data) {
      toast.success(data.message || "Admin created successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to create admin");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function updateAdminUser(adminId, payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "PUT",
      `/admin/users/${adminId}`,
      payload,
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Admin updated successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to update admin");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function deleteAdminUser(adminId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/users/${adminId}`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Admin deactivated successfully");
      return data;
    } else {
      toast.error(data?.message || "Failed to deactivate admin");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function hardDeleteAdminUser(adminId, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/users/${adminId}/permanent`,
      {},
      config
    );
    if (status === 200 && data) {
      toast.success(data.message || "Admin permanently deleted");
      return data;
    } else {
      toast.error(data?.message || "Failed to delete admin");
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function getAvailablePermissions(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/users/permissions`,
      {},
      config
    );
    if (status === 200 && data) {
      return data;
    }
  } catch (error) {
    console.error("Failed to fetch permissions:", error);
  }
}

// ===========================================
// Notification Management
// ===========================================

async function sendNotificationToUser(payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/notification/send-to-user`,
      payload,
      config
    );
    if (status === 200 || status === 201) {
      return { success: true, ...data };
    } else {
      return { success: false, message: data?.message || "Failed to send notification" };
    }
  } catch (error) {
    handleApiError(error);
    return { success: false, message: "Failed to send notification" };
  }
}

async function sendNotificationToAll(payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/notification/send-to-all`,
      payload,
      config
    );
    if (status === 200 || status === 201) {
      return { success: true, ...data };
    } else {
      return { success: false, message: data?.message || "Failed to send notification" };
    }
  } catch (error) {
    handleApiError(error);
    return { success: false, message: "Failed to send notification" };
  }
}

async function sendNotificationToMultiple(payload, config = {}) {
  try {
    const { data, status } = await makeRequest(
      "POST",
      `/admin/notification/send-to-multiple`,
      payload,
      config
    );
    if (status === 200 || status === 201) {
      return { success: true, ...data };
    } else {
      return { success: false, message: data?.message || "Failed to send notification" };
    }
  } catch (error) {
    handleApiError(error);
    return { success: false, message: "Failed to send notification" };
  }
}

async function getAdminNotifications(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const { data, status } = await makeRequest(
      "GET",
      `/admin/notifications?${queryParams}`,
      {},
      config
    );
    if (status === 200 && data) {
      // Normalize dates that might be in MongoDB Extended JSON format
      const notifications = (data.data || []).map(notification => ({
        ...notification,
        createdAt: extractDateString(notification.createdAt),
        updatedAt: extractDateString(notification.updatedAt),
      }));
      return { success: true, ...data, data: notifications };
    }
  } catch (error) {
    console.error("Failed to fetch notifications:", error);
    return { success: false, data: [] };
  }
}

// Helper to extract date string from various MongoDB date formats
function extractDateString(date) {
  if (!date) return null;

  // Already a valid ISO string
  if (typeof date === 'string') {
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : date;
  }

  // Handle timestamp number (milliseconds)
  if (typeof date === 'number') return new Date(date).toISOString();

  // Handle Date object
  if (date instanceof Date) return date.toISOString();

  // Handle object formats
  if (typeof date === 'object') {
    // MongoDB Extended JSON: { $date: "..." } or { $date: { $numberLong: "..." } }
    if (date.$date) {
      if (typeof date.$date === 'string') return date.$date;
      if (date.$date.$numberLong) return new Date(parseInt(date.$date.$numberLong)).toISOString();
    }

    // Handle object with toISOString method
    if (typeof date.toISOString === 'function') {
      return date.toISOString();
    }

    // Try to create Date from object directly (handles most serialized Date objects)
    try {
      const attemptDate = new Date(date);
      if (!isNaN(attemptDate.getTime())) {
        return attemptDate.toISOString();
      }
    } catch (e) {
      // ignore
    }

    // Log unknown format for debugging
    console.log("Unknown date format:", JSON.stringify(date));
  }

  return null;
}

async function getNotificationStats(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "GET",
      `/admin/notifications/stats`,
      {},
      config
    );
    if (status === 200 && data) {
      return { success: true, ...data };
    }
  } catch (error) {
    console.error("Failed to fetch notification stats:", error);
    return { success: false };
  }
}

async function searchUsersForNotification(params = {}, config = {}) {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const { data, status } = await makeRequest(
      "GET",
      `/admin/notification/search-users?${queryParams}`,
      {},
      config
    );
    if (status === 200 && data) {
      // Response structure is { data: [...] } - array of users directly
      const users = data.data || [];
      return { success: true, data: users };
    }
    return { success: false, data: [] };
  } catch (error) {
    console.error("Failed to search users:", error);
    return { success: false, data: [] };
  }
}

async function deleteAllNotifications(config = {}) {
  try {
    const { data, status } = await makeRequest(
      "DELETE",
      `/admin/notifications/all`,
      {},
      config
    );
    if (status === 200 && data) {
      return { success: true, ...data };
    }
    return { success: false };
  } catch (error) {
    console.error("Failed to delete all notifications:", error);
    return { success: false };
  }
}

// Helper to extract string ID from various ObjectId formats
function extractIdString(id) {
  if (!id) return null;
  if (typeof id === 'string') return id;
  if (id.$oid) return id.$oid;
  // Handle MongoDB ObjectId with buffer
  if (id.buffer && typeof id.buffer === 'object') {
    // Convert buffer bytes to hex string
    const bytes = Object.values(id.buffer);
    return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
  }
  if (typeof id.toHexString === 'function') return id.toHexString();
  if (typeof id.toString === 'function') {
    const str = id.toString();
    if (str !== '[object Object]') return str;
  }
  return null;
}

export const allAPiServicesCall = {
  loginAdmin,
  getProfileDetails,
  editProfile,
  editProfilePicure,
  changePassword,
  forgetPassword,
  resetPassword,
  getNews,
  submitNewsForReview,
  approveNewsRevision,
  requestNewsCorrections,
  scheduleNewsRevision,
  publishNewsRevision,
  revalidateNewsRevision,
  getNewsroomCapabilities,
  getNewsroomControl,
  updateNewsroomControl,
  listNewsroomRuns,
  startNewsroomRun,
  getNewsroomRunEvidence,
  cancelNewsroomRun,
  retryNewsroomRun,
  getNewsDetails,
  addNews,
  addNewsCoverPic,
  uploadNewsVideo,
  deleteNews,
  addTestimonial,
  addTestimonialProfilePic,
  getTestimonialList,
  deleteTestimonial,
  getHotSongCard,
  deleteHotAlbum,
  getTopChartCard,
  deleteTopChart,
  getFeedback,
  displayLyrics,
  addHotSongs,
  albumSongs,
  artistSongs,
  addHotAlbums,
  deleteActualHotAlbum,
  /**V2 API Services */
  addMostPopularSongs,
  deleteMostPopularSongs,
  createAdminPlaylist,
  getTrendingArtists,
  getRecommendedArtists,
  getSearchArtist,
  addTrendingArtist,
  addRecommendedArtist,
  deleteTrendingArtist,
  deleteRecommendedArtist,
  getTopExploreAlbums,
  getSearchAlbum,
  addTopAlbum,
  deleteTopExploreAlbum,
  getAllAdminPlaylist,
  getPlaylistSong,
  getSongRecommendedList,
  getPopularSongs,
  getSearchSong,
  addRecommendedSong,
  deleteRecommendedSong,
  getMostPlayedSong,
  addMostPlayedSong,
  deleteMostPlayedSong,
  deleteAdminPlaylist,
  patchRemoveAdminPlaylistSong,
  patchUpdateAdminPlaylist,
  uploadPlaylistImage,
  toggleTopPlaylist,
  toggleExplorePlaylist,
  patchUpdatePlaylistPriority,
  getGenres,
  getPageConfigTargets,
  getPageConfig,
  savePageConfig,
  addGenres,
  createGenre,
  getGenreById,
  updateGenre,
  deleteGenre,
  addSongToAdminPlayList,
  getAllQuizs,
  uploaduizCsv,
  getContactus,
  addQuiz,
  getTopArtists,
  addTopArtist,
  deleteTopArtist,
  getTopSong,
  addTopSong,
  deleteTopSong,
  getNewReleaseSong,
  getAutomatedReleaseControl,
  updateAutomatedReleaseControl,
  runAutomatedReleasePublication,
  getAllPlaylistBio,
  getAllContributeToLyrics,
  getLyricsById,
  editArtistBio,
  getArtistBioDetails,
  uploadNewsImage,
  putUpdateNews,
  generateNewsArticle,
  suggestNewsTopics,
  uploadAuthorImage,
  researchNewsTopics,
  writeNewsArticle,
  getNewsGenerationStatus,
  getAuthors,
  addAuthor,
  updateAuthor,
  deleteAuthor,
  uploadAuthorPersonaImage,
  seedAuthorPersonas,
  uploadBannerImage,
  approveOrrejectLyrics,
  getNewsSubscriber,
  getArtistSongAndAlbums,
  getBanners,
  addBanner,
  editBanner,
  deleteBanner,
  toggleStatusFun,
  getYoutubeVideos,
  getSearchYoutubeVideos,
  deleteContribution,

  // podcasts and episodes
  getPodcasts,
  createPodcast,
  editPodcast,
  deletePodcast,
  getEpisodes,
  createEpisode,
  deleteEpisode,
  deleteEpisodeAudio,
  editEpisode,
  updateEpisodeAudio,
  addToMusicVideoSection,
  removeFromMusicVideoSection,
  updateFeaturedMusicVideoGenres,
  addNewReleaseSong,
  deleteNewReleaseSong,

  // Add and Edit Artist Pages by DigiMedia Code
  getArtistById,
  createArtist,
  updateArtist,
  uploadArtistCsv,
  deleteArtist,
  listArtists,

  // QUIZ Section
  getQuizById,
  updateQuiz,

  // ListSongs,
  listSongs,
  deleteSong,
  listAlbumsByArtist,
  listArtistsSimple,
  createSong,
  uploadSongImage,
  getSongById,
  updateSong,
  patchUpdateTrendingArtistPriority,
  patchUpdateRecArtistPriority,
  patchUpdateTopAlbumPriority,

  // Song Lyrics Contributions (for existing songs)
  getAllSongLyricsContributions,
  getSongLyricsContributionById,
  processSongLyricsContribution,
  deleteSongLyricsContribution,
  bulkProcessSongLyricsContributions,

  // Album Contributions
  getAllAlbumContributions,
  getAlbumContributionById,
  processAlbumContribution,
  deleteAlbumContribution,
  bulkProcessAlbumContributions,

  // Album Management
  createAlbum,
  uploadAlbumImage,
  getAlbums,
  updateAlbum,
  deleteAlbum,

  // Song Contributions
  getAllSongContributions,
  getSongContributionById,
  processSongContribution,
  deleteSongContribution,
  bulkProcessSongContributions,

  // Artist Bio Contributions
  getAllArtistBioContributions,
  getArtistBioContributionById,
  processArtistBioContribution,
  deleteArtistBioContribution,
  bulkProcessArtistBioContributions,

  // Video Contributions
  getAllVideoContributions,
  getVideoContributionById,
  processVideoContribution,
  deleteVideoContribution,
  bulkProcessVideoContributions,

  // User Activity Tracking
  getUsersActivity,
  getUserActivityDetail,
  getActivityLeaderboard,
  getEngagementReport,

  // Cache Management
  clearAllCache,
  clearCacheByPattern,
  clearNewsCache,
  clearPlaylistsCache,
  pingRedis,

  // Content Pre-warming (Real-time)
  startPrewarmJob,
  getPrewarmJobStatus,
  getAllPrewarmJobs,
  cancelPrewarmJob,
  checkArtistContent,
  searchArtistsForPrewarm,
  previewCsvArtists,

  // Batch Pre-warming (50% cheaper, async)
  startBatchPrewarmJob,
  getBatchJobStatus,
  getAllBatchJobs,
  cancelBatchJob,
  deleteBatchJob,
  rerunBatchJob,
  regenerateBadBios,

  // Tags Management
  getTags,
  createTag,
  updateTag,
  deleteTag,
  getTagBySlug,
  getFeaturedTags,

  // Admin User Management
  getAdminProfile,
  listAdminUsers,
  getAdminById,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  hardDeleteAdminUser,
  getAvailablePermissions,

  // Notification Management
  sendNotificationToUser,
  sendNotificationToAll,
  sendNotificationToMultiple,
  getAdminNotifications,
  getNotificationStats,
  searchUsersForNotification,
  deleteAllNotifications,
};
